import Cookies from 'js-cookie';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import {
   AlertActivateDto,
   AlertDeactivateDto,
   AlertDeleteDto,
   AlertDeleteResponse,
   AletrsIndexResponse,
   CandidateAlert,
} from './types';
import { AsyncState } from 'shared/libs/state';
import { ManageAlertsApiService } from './ManageAlertsApiService';
import { PaginationControl } from 'shared/components/pagination';

export class ManageAlertsStore {
   readonly loadAlertsState = new AsyncState<AletrsIndexResponse>();
   readonly deleteAlertState = new AsyncState<AlertDeleteResponse>();
   readonly activateAlertState = new AsyncState<AlertDeleteResponse>();
   readonly deactivateAlertState = new AsyncState<AlertDeleteResponse>();

   readonly pagination = new PaginationControl();
   private readonly PER_PAGE = 10;

   rawAlerts: CandidateAlert[] = [];
   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawAlerts: observable,
         alerts: computed,
         totalPages: computed,
         canNext: computed,
         canPrev: computed,
         recordsCount: computed,
         coef: computed,
      });
   }

   get totalPages(): number {
      return Math.ceil(this.rawAlerts.length / this.PER_PAGE);
   }

   get alerts(): CandidateAlert[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.rawAlerts.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get canPrev(): boolean {
      return this.pagination.page > 0;
   }

   get canNext(): boolean {
      const next = this.pagination.page + 1;
      return next < this.totalPages;
   }

   get recordsCount(): string {
      const firstRecord = this.pagination.page * this.PER_PAGE + 1;
      let lastRecord = firstRecord + this.PER_PAGE - 1;
      if (lastRecord > this.rawAlerts.length) {
         lastRecord = this.rawAlerts.length;
      }
      return `${firstRecord} - ${lastRecord}`;
   }

   get coef(): number {
      return this.pagination.page * this.PER_PAGE + 1;
   }

   onNext(): void {
      if (this.canNext) {
         this.pagination.setPage(this.pagination.page + 1);
      }
   }

   onPrev(): void {
      if (this.canPrev) {
         this.pagination.setPage(this.pagination.page - 1);
      }
   }

   async loadAlerts(): Promise<void> {
      await this.loadAlertsState.run(() => ManageAlertsApiService.loadAlerts(null, {}), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.rawAlerts = resp.response.alerts;
               });
            }
         },
      });
   }

   async activateAlert({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AlertActivateDto;
      onSuccess?: (res: AlertDeleteResponse) => void;
      onError?: (e: string) => void;
   }) {
      await this.activateAlertState.run(() => ManageAlertsApiService.activateAlert(dto), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               onSuccess?.(resp);
               this.loadAlerts();
            } else {
               onError?.(resp.message);
            }
         },
         onError: (err) => {
            onError?.(err.message);
         },
      });
   }

   async deactivateAlert({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AlertDeactivateDto;
      onSuccess?: (res: AlertDeleteResponse) => void;
      onError?: (e: string) => void;
   }) {
      await this.deactivateAlertState.run(() => ManageAlertsApiService.deactivateAlert(dto), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               onSuccess?.(resp);
               this.loadAlerts();
            } else {
               onError?.(resp.message);
            }
         },
         onError: (err) => {
            onError?.(err.message);
         },
      });
   }

   async deleteAlert({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AlertDeleteDto;
      onSuccess?: (res: AlertDeleteResponse) => void;
      onError?: (e: string) => void;
   }) {
      await this.deleteAlertState.run(() => ManageAlertsApiService.deleteAlert(dto), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               onSuccess?.(resp);
               this.loadAlerts();
            } else {
               onError?.(resp.message);
            }
         },
         onError: (err) => {
            onError?.(err.message);
         },
      });
   }
}
